/**
 * One team's card on the setup view: name, colour, roster and score.
 *
 * Used while teams are being built (draft → lobby). The live dashboard has its own
 * row layout, but reuses {@link TeamRosterEditor} from here so mid-game roster
 * fixes behave identically to setup-time ones.
 */

import { useState, useMemo } from 'react';
import type { TrlDisruptionRecord, TrlSabotageType, TrlTeam } from '@swtorbit/shared-socket-events';
import { getTrlSabotageIconUrl } from '@swtorbit/shared-assets';
import { TeamColorGrid } from './TeamColorGrid';

/**
 * A team's placed sabotage, as the cards read it: the type (which resolves the
 * artwork) alongside the text, e.g. "Treasure Thief · Planet 4".
 */
export interface TrlSabotagePlacementSummary {
  type: TrlSabotageType;
  label: string;
}

export interface TeamRosterEditorProps {
  team: TrlTeam;
  playerNameById: (playerId: string) => string;
  unassignedPlayers: { playerId: string; name: string }[];
  /** Active disruption blocks keyed by victim playerId. */
  activeBlocks: Map<string, TrlDisruptionRecord>;
  onAssign: (teamId: string, playerId: string) => void;
  onRemove: (teamId: string, playerId: string) => void;
  onSetCaptain: (teamId: string, playerId: string) => void;
  onClearBlock: (playerId: string) => void;
}

/**
 * The member list plus the add-player control.
 *
 * Extracted from the card because the live dashboard needs exactly this, in a
 * drawer, so an admin can fix a roster mid-game without leaving the live view.
 */
export function TeamRosterEditor({
  team,
  playerNameById,
  unassignedPlayers,
  activeBlocks,
  onAssign,
  onRemove,
  onSetCaptain,
  onClearBlock,
}: TeamRosterEditorProps) {
  const [assignId, setAssignId] = useState('');

  return (
    <>
      <div className="d-flex flex-column gap-2xs">
        {team.members.length === 0 ? (
          <span className="fs-sm text-slate">No players assigned.</span>
        ) : (
          team.members.map((member) => {
            const isCaptain = team.captainPlayerId === member.playerId;
            const block = activeBlocks.get(member.playerId);
            return (
              <div
                key={member.playerId}
                className="d-flex align-items-center justify-content-between gap-2xs p-2xs rounded-base bg-space bg-o-50"
              >
                <span className="d-flex align-items-center gap-2xs text-white fs-sm">
                  {isCaptain && (
                    <span className="badge badge--sm" title="Team captain — makes the map selections">
                      ★ Captain
                    </span>
                  )}
                  {block && (
                    <span
                      className="badge badge--sm badge--red"
                      title={`Scanner disrupted by ${block.disruptorName} — blocked until a teammate hits them with Sparkle Powder`}
                    >
                      BLOCKED
                    </span>
                  )}
                  {playerNameById(member.playerId)}
                </span>
                <div className="d-flex align-items-center gap-2xs">
                  {block && (
                    <button
                      className="btn btn--amber btn--sm"
                      title={`Clear the block (disrupted by ${block.disruptorName})`}
                      onClick={() => onClearBlock(member.playerId)}
                    >
                      Clear
                    </button>
                  )}
                  {!isCaptain && (
                    <button
                      className="btn btn--outline btn--slate btn--sm"
                      title="Make this player the team captain"
                      onClick={() => onSetCaptain(team.id, member.playerId)}
                    >
                      ★
                    </button>
                  )}
                  <button
                    className="btn btn--outline btn--slate btn--sm"
                    title="Remove from team"
                    onClick={() => onRemove(team.id, member.playerId)}
                  >
                    ↩
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="d-flex gap-2xs align-items-center flex-wrap">
        <select
          className="select"
          value={assignId}
          onChange={(e) => setAssignId(e.target.value)}
          style={{ flex: 1, minWidth: '140px' }}
        >
          <option value="">-- Add player --</option>
          {unassignedPlayers.map((p) => (
            <option key={p.playerId} value={p.playerId}>
              {p.name}
            </option>
          ))}
        </select>
        <button
          className="btn btn--sm"
          disabled={!assignId}
          onClick={() => {
            if (assignId) {
              onAssign(team.id, assignId);
              setAssignId('');
            }
          }}
        >
          ➕ Add
        </button>
      </div>
    </>
  );
}

interface TeamCardProps extends TeamRosterEditorProps {
  /** All teams in the game (for the colour grid's taken colours). */
  teams: TrlTeam[];
  availableNames: string[];
  /** Lobby-only: this team's chosen starting planet label, or null if not yet picked. */
  startStatus?: { inLobby: boolean; planetLabel: string | null };
  /**
   * Minimum members this team needs, or null when there is no minimum. Set only
   * when simultaneous triangulation broadcast is on, which needs one player per
   * triangulation point.
   */
  minMembers?: number | null;
  /**
   * The team's sabotage readout: `show` when sabotage is enabled (or one is
   * placed), `placement` = the type + "Treasure Thief · Planet 4", null while
   * none is placed.
   */
  sabotageStatus: { show: boolean; placement: TrlSabotagePlacementSummary | null; inLobby: boolean };
  onRename: (teamId: string, name: string) => void;
  onRecolor: (teamId: string, color: string) => void;
  onDelete: (teamId: string) => void;
}

export function TeamCard({
  team,
  teams,
  availableNames,
  playerNameById,
  unassignedPlayers,
  startStatus,
  minMembers,
  activeBlocks,
  sabotageStatus,
  onRename,
  onRecolor,
  onDelete,
  onAssign,
  onRemove,
  onSetCaptain,
  onClearBlock,
}: TeamCardProps) {
  const [showColors, setShowColors] = useState(false);

  // Names selectable for rename: available names plus this team's own name.
  const renameOptions = useMemo(() => {
    const set = new Set([team.name, ...availableNames]);
    return Array.from(set);
  }, [team.name, availableNames]);

  const understaffed = minMembers != null && team.members.length < minMembers;

  return (
    <div className="trl-teams__card">
      <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap">
        <button
          type="button"
          className="icon icon--2xl p-0 rounded-full border-xl border-white"
          style={{ background: team.color ?? 'transparent', cursor: 'pointer', flexShrink: 0 }}
          title="Change team colour"
          aria-label="Change team colour"
          aria-expanded={showColors}
          onClick={() => setShowColors((open) => !open)}
        />
        <select
          className="select"
          value={team.name}
          onChange={(e) => onRename(team.id, e.target.value)}
          style={{ flex: 1, minWidth: '140px' }}
        >
          {renameOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <div className="d-flex gap-2xs align-items-center">
          <span
            className={`badge badge--sm${understaffed ? ' text-red' : ''}`}
            title={
              understaffed
                ? `Simultaneous triangulation broadcast needs at least ${minMembers} players per team — the game cannot start until this team has ${minMembers}.`
                : undefined
            }
          >
            {team.members.length}
            {minMembers ? `/${minMembers}` : ''}
          </span>
          <span className="badge badge--sm">{team.totalScore} pts</span>
          <button className="btn btn--red btn--sm" onClick={() => onDelete(team.id)} title="Delete team">
            🗑️
          </button>
        </div>
      </div>

      {showColors && (
        <TeamColorGrid
          teams={teams}
          selected={team.color}
          ownTeamId={team.id}
          onSelect={(color) => {
            setShowColors(false);
            if (color !== team.color?.toLowerCase()) onRecolor(team.id, color);
          }}
        />
      )}

      <TeamRosterEditor
        team={team}
        playerNameById={playerNameById}
        unassignedPlayers={unassignedPlayers}
        activeBlocks={activeBlocks}
        onAssign={onAssign}
        onRemove={onRemove}
        onSetCaptain={onSetCaptain}
        onClearBlock={onClearBlock}
      />

      {startStatus?.inLobby && (
        <div className="d-flex align-items-center justify-content-between gap-xs p-2xs rounded-base bg-space bg-o-50">
          <span className="fs-sm text-slate">Starting planet</span>
          {startStatus.planetLabel ? (
            <span className="text-green-dark fs-sm">✅ {startStatus.planetLabel}</span>
          ) : (
            <span className="text-amber fs-sm">⏳ Captain choosing…</span>
          )}
        </div>
      )}

      {sabotageStatus.show && (
        <div className="d-flex align-items-center justify-content-between gap-xs p-2xs rounded-base bg-space bg-o-50">
          <span className="fs-sm text-slate">Sabotage</span>
          {sabotageStatus.placement ? (
            <span className="fs-sm d-inline-flex align-items-center gap-2xs">
              <img
                className="trl-sabotage-glyph"
                src={getTrlSabotageIconUrl(sabotageStatus.placement.type)}
                alt=""
                aria-hidden="true"
              />
              {sabotageStatus.placement.label}
            </span>
          ) : (
            <span className="text-slate fs-sm">
              {sabotageStatus.inLobby ? 'None placed (optional)' : 'None'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default TeamCard;
